/** The rules actually in force, and the fenced way to change them (DEC-07). */

import { useEffect, useState } from 'react'
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { EditRegular, PlayRegular } from '@fluentui/react-icons'
import {
  CodeBlock,
  DataTableShell,
  EmptyState,
  KeyValueList,
  KeyValueRow,
  LoadingState,
  MetricPill,
  MonoText,
  SectionCard,
  SectionLabel,
  StatusBadge,
  StepPipeline,
  formatTimestamp,
} from '../platform'
import type { PipelineStep } from '../platform'
import { ApiError, api } from '../api'
import type { DryRun, PolicyVersion, RoutingPolicyView, RoutingRuleView } from '../api'

const RULE_STATUS = {
  ACTIVE: 'success',
  TRANSITIONAL: 'info',
  SUNSET: 'error',
} as const

const STATES = ['ACTIVE', 'TRANSITIONAL', 'SUNSET'] as const
const AUTHORITIES = ['tim', 'dcs', 'hubject'] as const

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  table: { minWidth: '860px' },
  pills: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  note: {
    display: 'block',
    marginTop: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
  },
  editor: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  rule: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: tokens.spacingHorizontalS,
    alignItems: 'end',
  },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  narrow: { maxWidth: '220px' },
})

export default function RoutingPolicy() {
  const styles = useStyles()
  const [data, setData] = useState<RoutingPolicyView | null>(null)
  const [versions, setVersions] = useState<PolicyVersion[]>([])
  const [error, setError] = useState<string | null>(null)

  const [draft, setDraft] = useState<RoutingRuleView[] | null>(null)
  const [note, setNote] = useState('')
  const [refused, setRefused] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const [probe, setProbe] = useState({ identifier: '', station_id: '' })
  const [dryRun, setDryRun] = useState<DryRun | null>(null)

  const load = () =>
    Promise.all([
      api.get<RoutingPolicyView>('/routing-policy'),
      api.get<{ items: PolicyVersion[] }>('/routing-policy/versions'),
    ])
      .then(([policy, history]) => {
        setData(policy)
        setVersions(history.items)
      })
      .catch((e) => setError(e.message))

  useEffect(() => {
    void load()
  }, [])

  if (error) return <EmptyState title="The policy cannot be read" description={error} />
  if (!data) return <LoadingState label="Reading the routing policy" />

  const rules = draft ?? data.rules
  const editing = draft !== null

  const change = (index: number, patch: Partial<RoutingRuleView>) =>
    setDraft(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)))

  const save = () => {
    setRefused(null)
    api
      .put<RoutingPolicyView>('/routing-policy', {
        rules: draft,
        note,
        expected_version: Number(data.version),
      })
      .then(() => {
        setConfirming(false)
        setDraft(null)
        setNote('')
        return load()
      })
      .catch((e) => {
        setConfirming(false)
        // The service says which rule and which invariant. Showing the message
        // it wrote beats inventing a shorter one that says less.
        setRefused(e instanceof ApiError ? e.problem.detail || e.problem.title : e.message)
      })
  }

  const run = () => {
    setRefused(null)
    api
      .post<DryRun>('/routing-policy/dry-run', { rules, ...probe })
      .then(setDryRun)
      .catch((e) =>
        setRefused(e instanceof ApiError ? e.problem.detail || e.problem.title : e.message),
      )
  }

  const steps: PipelineStep[] = (dryRun?.steps ?? []).map((step, i) => ({
    key: `${i}-${step.step}`,
    label: step.step,
    state: step.matched ? 'done' : 'skipped',
  }))

  return (
    <div className={styles.page}>
      {(data.warnings ?? []).length > 0 ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Waiting on BMW</MessageBarTitle>
            {(data.warnings ?? []).join('; ')}. The rule stays in force until a date
            arrives; setting one is what makes it stop on its own.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {refused ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>That policy was not saved</MessageBarTitle>
            {refused}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <DataTableShell
        title={editing ? 'Rules, editing' : 'Rules'}
        description="Which authority answers for an identifier at a charge point. A change is a new version: it carries who saved it and why, and every decision records the version that produced it."
        controls={
          <div className={styles.pills}>
            <MetricPill label="Version" value={data.version} />
            <MetricPill label="Hash" value={data.hash.slice(0, 12)} />
            {data.editable && !editing ? (
              <Button icon={<EditRegular />} onClick={() => setDraft([...data.rules])}>
                Edit
              </Button>
            ) : null}
          </div>
        }
        footerStart={`${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`}
      >
        <Table size="small" className={styles.table}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Prefix</TableHeaderCell>
              <TableHeaderCell>Scheme</TableHeaderCell>
              <TableHeaderCell>Authority</TableHeaderCell>
              <TableHeaderCell>Estate</TableHeaderCell>
              <TableHeaderCell>State</TableHeaderCell>
              <TableHeaderCell>Sunset</TableHeaderCell>
              <TableHeaderCell>Enabled</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.map((rule, index) => (
              <TableRow key={rule.prefix}>
                <TableCell>
                  <MonoText>{rule.prefix}</MonoText>
                </TableCell>
                <TableCell>{rule.scheme}</TableCell>
                <TableCell>
                  {editing ? (
                    <Dropdown
                      size="small"
                      value={rule.authority}
                      selectedOptions={[rule.authority]}
                      onOptionSelect={(_, d) =>
                        d.optionValue && change(index, { authority: d.optionValue })
                      }
                    >
                      {AUTHORITIES.map((a) => (
                        <Option key={a} value={a}>
                          {a}
                        </Option>
                      ))}
                    </Dropdown>
                  ) : (
                    rule.authority
                  )}
                </TableCell>
                <TableCell>
                  {editing ? (
                    <Input
                      size="small"
                      value={rule.requires_estate ?? ''}
                      onChange={(_, d) =>
                        change(index, { requires_estate: d.value || null })
                      }
                    />
                  ) : (
                    (rule.requires_estate ?? 'any')
                  )}
                </TableCell>
                <TableCell>
                  {editing ? (
                    <Dropdown
                      size="small"
                      value={rule.state}
                      selectedOptions={[rule.state]}
                      onOptionSelect={(_, d) =>
                        d.optionValue &&
                        change(index, { state: d.optionValue as RoutingRuleView['state'] })
                      }
                    >
                      {STATES.map((state) => (
                        <Option key={state} value={state}>
                          {state}
                        </Option>
                      ))}
                    </Dropdown>
                  ) : (
                    <StatusBadge status={RULE_STATUS[rule.state]} label={rule.state} />
                  )}
                </TableCell>
                <TableCell>
                  {editing ? (
                    <Input
                      size="small"
                      value={rule.sunset_at ?? ''}
                      placeholder="2026-12-31T23:59:59Z"
                      onChange={(_, d) => change(index, { sunset_at: d.value || null })}
                    />
                  ) : (
                    <MonoText tone="muted">
                      {rule.sunset_at ?? 'not set by BMW yet'}
                    </MonoText>
                  )}
                </TableCell>
                <TableCell>
                  {editing ? (
                    <Switch
                      checked={rule.enabled}
                      onChange={(_, d) => change(index, { enabled: d.checked })}
                    />
                  ) : (
                    (rule.enabled ? 'yes' : 'no')
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataTableShell>

      <Caption1 className={styles.note}>
        A rule in SUNSET denies before any authority is called. An 847xx prefix cannot be
        routed away from TIM or stripped of its estate: the service refuses to save it
        (ADR-001, CR 001).
      </Caption1>

      {editing ? (
        <SectionCard
          title="Try it before you save it"
          description="Classifies one identifier against the rules above, without saving anything. The identifier is treated exactly as one presented at a charge point: never stored, never logged."
        >
          <div className={styles.editor}>
            <div className={styles.rule}>
              <Field label="Identifier">
                <Input
                  value={probe.identifier}
                  onChange={(_, d) => setProbe({ ...probe, identifier: d.value })}
                  placeholder="Full card number"
                />
              </Field>
              <Field label="Charge point">
                <Input
                  value={probe.station_id}
                  onChange={(_, d) => setProbe({ ...probe, station_id: d.value })}
                  placeholder="DE*E4E*E*0150C113"
                />
              </Field>
            </div>
            <div className={styles.actions}>
              <Button
                icon={<PlayRegular />}
                onClick={run}
                disabled={!probe.identifier || !probe.station_id}
              >
                Dry run
              </Button>
            </div>

            {dryRun ? (
              <>
                <StepPipeline steps={steps} />
                <KeyValueList divided>
                  <KeyValueRow label="Route" value={dryRun.route} />
                  <KeyValueRow
                    label="Authority"
                    value={dryRun.authority ?? 'none would be called'}
                  />
                  <KeyValueRow label="Refused" value={dryRun.refused ? 'yes' : 'no'} />
                  <KeyValueRow label="Basis" value={dryRun.basis ?? 'none'} />
                </KeyValueList>
              </>
            ) : null}
          </div>
        </SectionCard>
      ) : null}

      {editing ? (
        <SectionCard
          title="Save this version"
          description="The note is required and stored verbatim. Why is half the record."
        >
          <div className={styles.editor}>
            <Field label="Why">
              <Input
                value={note}
                onChange={(_, d) => setNote(d.value)}
                placeholder="84710 sunset agreed with BMW"
              />
            </Field>
            <div className={styles.actions}>
              <Button appearance="primary" disabled={!note} onClick={() => setConfirming(true)}>
                Review and save
              </Button>
              <Button
                onClick={() => {
                  setDraft(null)
                  setNote('')
                  setDryRun(null)
                  setRefused(null)
                }}
              >
                Discard
              </Button>
            </div>
          </div>
        </SectionCard>
      ) : null}

      <Dialog open={confirming} onOpenChange={(_, d) => setConfirming(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Save routing version {Number(data.version) + 1}?</DialogTitle>
            <DialogContent>
              <SectionLabel>What changes</SectionLabel>
              <CodeBlock wrap maxHeight={320}>
                {describe(data.rules, rules)}
              </CodeBlock>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirming(false)}>Cancel</Button>
              <Button appearance="primary" onClick={save}>
                Save version {Number(data.version) + 1}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <SectionCard
        title="History"
        description="Every version, newest first, with the note its author had to write."
      >
        <KeyValueList divided>
          {versions.map((version) => (
            <KeyValueRow
              key={version.version}
              label={`v${version.version}, ${version.author ?? 'unknown'}`}
              value={`${version.note ?? ''} (${formatTimestamp(version.created_at)})`}
            />
          ))}
        </KeyValueList>
      </SectionCard>

      <SectionCard
        title="Excluded stations"
        description="Inherited from the OCPP Server. These charge points authorize locally and never reach this service, so a station missing from the traffic here is not necessarily a fault."
      >
        {data.excluded_stations.length === 0 ? (
          <Caption1 className={styles.note}>None recorded here.</Caption1>
        ) : (
          <CodeBlock copyValue={data.excluded_stations.join('\n')} maxHeight={280}>
            {data.excluded_stations.join('\n')}
          </CodeBlock>
        )}
      </SectionCard>
    </div>
  )
}

/** The diff, in words, for the confirmation dialog.
 *
 *  Computed here from the two rule sets rather than asked of the service: the
 *  service has not been given the draft yet, and the whole point of this
 *  dialog is to show what would be sent before sending it. */
function describe(before: RoutingRuleView[], after: RoutingRuleView[]): string {
  const old = new Map(before.map((rule) => [rule.prefix, rule]))
  const now = new Map(after.map((rule) => [rule.prefix, rule]))
  const lines: string[] = []

  for (const [prefix, rule] of now) {
    const previous = old.get(prefix)
    if (!previous) {
      lines.push(`+ ${prefix} added, ${rule.authority}, ${rule.state}`)
      continue
    }
    for (const field of [
      'scheme', 'authority', 'requires_estate', 'state', 'enabled', 'sunset_at',
    ] as const) {
      if (previous[field] !== rule[field]) {
        lines.push(`~ ${prefix} ${field}: ${String(previous[field])} to ${String(rule[field])}`)
      }
    }
  }
  for (const prefix of old.keys()) {
    if (!now.has(prefix)) lines.push(`- ${prefix} removed`)
  }

  return lines.length ? lines.join('\n') : 'Nothing changes.'
}
